import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from 'components/UI/Modal';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { Border, Main, Text } from 'styles/themeColors';
import { FontWeight } from 'lib/styles';
import { TEXT_LABELS } from 'constants/order';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTime } from 'luxon';
import { useAPI } from 'lib/API';
import { updateSubscriptionModification } from 'requests/consignment';
import { renderToast, toast } from 'components/UI/ToastNotification';

const LabelText = styled.span`
  display: block;
  font-weight: ${FontWeight.medium};
  text-align: left;
  margin-bottom: 5px;
  font-size: 15px;
  color: ${Text.primary};
`;

const LabelTextError = styled.span`
  display: block;
  font-size: 12px;
  color: ${Main.error2};
  margin-top: 4px;
`;

const TextArea = styled.textarea`
  border: 1px solid ${Border.main};
  border-radius: 6px;
  padding: 7px;
  color: ${Text.primary};
  resize: vertical;
`;

export interface Props {
  show: boolean;
  onClose: () => void;
  consignmentId?: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  disabled?: boolean;
  onSave?: () => void;
}

const SubscriptionModificationModal = ({
  show,
  onClose,
  consignmentId,
  subscriptionStartDate = '',
  subscriptionEndDate = '',
  disabled = false,
  onSave,
}: Props) => {
  const [endDate, setEndDate] = useState(subscriptionEndDate);
  const [reason, setReason] = useState('');
  const [endDateError, setEndDateError] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState<'end' | null>(null);

  const saveReq = useAPI({
    deferFn: updateSubscriptionModification,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'Subscription modified successfully');
      onClose();
      onSave?.();
    },
    onReject: (err: any) => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  const handleClose = () => {
    setEndDate(subscriptionEndDate);
    setReason('');
    setEndDateError(false);
    onClose();
  };

  const handleEndDateChange = (date: DateTime | null) => {
    const newDate = date?.toISO() || '';
    const isInvalid = (!!subscriptionEndDate && !!newDate && DateTime.fromISO(newDate) <= DateTime.fromISO(subscriptionEndDate)) && false; // TODO: removing this validation for now for testig purpose.
    setEndDateError(isInvalid);
    setEndDate(newDate);
  };

  const handleConfirm = () => {
    saveReq.run(consignmentId, endDate, reason);
  };

  return (
    <Modal
      showButtons
      showModal={show}
      title={TEXT_LABELS.SUBSCRIPTION_MODIFICATION}
      onClose={handleClose}
      onClick={handleConfirm}
      disabled={disabled || endDateError || endDate === subscriptionEndDate || saveReq.isLoading}
      ghostButtonText={TEXT_LABELS.SUBSCRIPTION_MODAL_CLOSE}
      primaryButtonText={TEXT_LABELS.SUBSCRIPTION_MODAL_MODIFY}
      width="560px"
      primaryButtonColor={Main.brandDark}
      color={Text.primary}
      borderColor={Text.primary}
    >
      <FlexboxContainer flexDirection="column">
        <FlexboxContainer flexDirection="row" gap="4px">
          <FlexboxContainer flexDirection="column" width="100%">
            <LabelText>{TEXT_LABELS.SUBSCRIPTION_START_DATE}</LabelText>
            <DatePicker
              value={subscriptionStartDate ? DateTime.fromISO(subscriptionStartDate) : null}
              format="yyyy MMM dd"
              disabled
              slotProps={{
                textField: { variant: 'outlined', size: 'small' },
              }}
              onChange={() => {}}
            />
          </FlexboxContainer>
          <FlexboxContainer flexDirection="column" width="100%">
            <LabelText>{TEXT_LABELS.SUBSCRIPTION_END_DATE}</LabelText>
            <DatePicker
              value={endDate ? DateTime.fromISO(endDate) : null}
              format="yyyy MMM dd"
              open={openDatePicker === 'end'}
              onOpen={() => setOpenDatePicker('end')}
              onClose={() => setOpenDatePicker(null)}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  error: endDateError,
                  onClick: () => setOpenDatePicker('end'),
                  InputProps: {
                    readOnly: true,
                    sx: { cursor: 'pointer' },
                  },
                },
              }}
              onChange={handleEndDateChange}
            />
            {endDateError && (
              <LabelTextError>{TEXT_LABELS.SUBSCRIPTION_END_DATE_ERROR}</LabelTextError>
            )}
          </FlexboxContainer>
        </FlexboxContainer>
        <FlexboxContainer flexDirection="column" margin="10px 0px 0px 0px">
          <LabelText>{TEXT_LABELS.SUBSCRIPTION_MODIFICATION_REASON}</LabelText>
          <TextArea
            maxLength={500}
            rows={5}
            value={reason}
            placeholder="Reason"
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
              }
            }}
          />
        </FlexboxContainer>
      </FlexboxContainer>
    </Modal>
  );
};

export default SubscriptionModificationModal;